import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import DiagnosticDataResults from "@/pages/diagnostic-edit/models/DiagnosticDataResults";

/**
 * Returns the user with the given id.
 * @param diagnosticIds of the diagnostics we want to fetch
 * @param deviceIds of the devices, the data is needed
 * @returns - Live Statusdata from the devices
 */
export function useStatusData(
  diagnosticIds: string[],
  deviceIds: string[],
  interval?: number | false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    queryKey: ["statusData", activeSessionId, diagnosticIds, deviceIds],

    queryFn: async ({ signal }): Promise<DiagnosticDataResults> => {
      const diagnosticPromises = diagnosticIds.map(diagnosticId =>
        Promise.all(
          deviceIds.map(deviceId =>
            geotabClient
              .get(
                "StatusData",
                {
                  fromDate: new Date(Date.now()),
                  toDate: new Date(Date.now()),
                  diagnosticSearch: { id: diagnosticId },
                  deviceSearch: { id: deviceId },
                },
                1,
                signal,
              )
              .then(data => ({ deviceId, data: data[0]?.["data"] })),
          ),
        ).then(deviceResults => ({
          diagnosticId,
          results: deviceResults.reduce(
            (acc, { deviceId, data }) => {
              acc[deviceId] = data;
              return acc;
            },
            {} as { [key: string]: number },
          ),
        })),
      );

      const diagnosticResults = await Promise.all(diagnosticPromises);

      return diagnosticResults.reduce((acc, { diagnosticId, results }) => {
        acc[diagnosticId] = results;
        return acc;
      }, {} as DiagnosticDataResults);
    },

    refetchInterval: interval ?? 30000,
    placeholderData: keepPreviousData,
    retry: true,
  });
}
